import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Fragment, useMemo, useState } from 'react';

import { fmt, fmtB, fmtDate } from '@dcc-erp/shared';
import type {
  CreateEmpBenefitSettlement,
  CreateEmpBenefitValuationV2,
  EmpBenefitApprovalStatus,
  EmpBenefitDisclosure,
  EmpBenefitMaturityProfile,
  EmpBenefitMonthlyEntryListRow,
  EmpBenefitSensitivityInput,
  EmpBenefitSettlementReason,
  EmpBenefitSettlementRow,
  EmpBenefitSource,
  EmpBenefitValuation,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { AppModal } from '../components/AppModal';
import { DecisionModal, type Decision } from '../components/approval/DecisionModal';
import { Can } from '../components/Can';
import { BranchSelect, DepartmentSelect } from '../components/dimensions';
import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import { useActiveCompanyId } from '../lib/queries/companies';
import {
  useCreateEmpBenefitSettlement,
  useCreateEmpBenefitValuation,
  useEmpBenefitDecide,
  useEmpBenefitDisclosure,
  useEmpBenefitMonthly,
  useEmpBenefitReverse,
  useEmpBenefitSettlements,
  useEmpBenefitSubmit,
  useEmpBenefitValuations,
  useEmpBenefitWhatIf,
  useProposeEmpBenefitMonthly,
  useUploadEmpBenefitValuationPdf,
  getEmpBenefitValuationPdfUrl,
  type EmpBenefitDocKind,
} from '../lib/queries/emp-benefit';
import { downloadXlsx, todayStamp } from '../lib/xlsx';

// Demo run-period — the prototype is anchored to พ.ค. 2026 and the period
// picker is a Phase 3d enhancement. Until then the "รัน TAS 19 ประจำเดือน"
// button targets this fixed period when called from the dashboard.
const DEMO_PERIOD = '202605';
const DEMO_YEAR = '2026';

type TabKey = 'dashboard' | 'actuarial' | 'periodic' | 'retirement' | 'disclosure';
type ReportStatus = 'current' | 'prior';
type PeriodicStatus = 'posted' | 'pending' | 'reversed';
type RetireeStatus = 'settled' | 'pending' | 'upcoming';

interface EbPlan {
  code: 'SEV' | 'GIFT';
  label: string;
  acctLiab: string;
  acctExp: string;
  tone: string;
  basis: string;
}

interface ActuaryAssumptions {
  discount: number;
  salaryInc: number;
  turnover: number;
  mortality: string;
  retire: number;
  fy: string;
}

interface ActuaryMovement {
  open: number;
  sc: number;
  ic: number;
  benefit_paid: number;
  actuarial: number;
}

interface ActuaryReport {
  id: string;
  // The real valuation UUID (id above is a display ref) — drives the PDF download.
  valuationId: string;
  pdfStorageKey: string | null;
  date: string;
  actuary: string;
  method: string;
  dbo: number;
  fv: number;
  plans: string;
  status: ReportStatus;
  assumptions: ActuaryAssumptions;
  movement?: ActuaryMovement;
}

export interface PeriodicRow {
  id: string;
  period: string;
  date: string;
  sc: number;
  ic: number;
  jv: string;
  journalEntryId: string | null;
  status: PeriodicStatus;
  approvalStatus: EmpBenefitApprovalStatus;
  currentStep: number;
}

// Display status for a settlement row in the register.
//   settled  = POSTED (JV booked)   pending = DRAFT past its settlement date
//   upcoming = DRAFT still in the future ("ใกล้เกษียณ")
function settlementDisplayStatus(s: EmpBenefitSettlementRow): RetireeStatus {
  if (s.status === 'POSTED') return 'settled';
  return new Date(s.settlementDate) <= new Date() ? 'pending' : 'upcoming';
}

const EB_PLANS: readonly EbPlan[] = [
  {
    code: 'SEV',
    label: 'ค่าชดเชยเกษียณ (Legal Severance Pay)',
    acctLiab: '20170027',
    acctExp: '60110092',
    tone: '#1a56a6',
    basis: 'พ.ร.บ. คุ้มครองแรงงาน 2541 (แก้ไข 2562) — สูงสุด 400 วัน',
  },
  {
    code: 'GIFT',
    label: 'รางวัลอายุงาน (Long Service Award)',
    acctLiab: '20170027',
    acctExp: '60110092',
    tone: '#7030A0',
    basis: 'นโยบายบริษัท — ทอง 1 บาท ที่ 10/20/30 ปี',
  },
];

// Zero-default report used when no actuarial valuation has been uploaded yet.
// All KPI values fall to 0 and the UI surfaces an empty-state banner with a
// "Upload Actuarial Report" CTA.
const EMPTY_REPORT: ActuaryReport = {
  id: '—',
  valuationId: '',
  pdfStorageKey: null,
  date: new Date().toISOString().slice(0, 10),
  actuary: '—',
  method: 'Projected Unit Credit',
  dbo: 0,
  fv: 0,
  plans: 'SEV+GIFT',
  status: 'current',
  assumptions: {
    discount: 0,
    salaryInc: 0,
    turnover: 0,
    mortality: '—',
    retire: 60,
    fy: String(new Date().getUTCFullYear() + 1),
  },
};

// Convert a server-side EmpBenefitValuation row to the ActuaryReport shape the
// existing prototype components consume. Keeps the DOM unchanged.
function valuationToReport(v: EmpBenefitValuation, status: ReportStatus): ActuaryReport {
  return {
    id: v.reportRef ?? v.id.slice(0, 12),
    valuationId: v.id,
    pdfStorageKey: v.pdfStorageKey ?? null,
    date:
      typeof v.valuationDate === 'string'
        ? v.valuationDate
        : new Date(v.valuationDate).toISOString().slice(0, 10),
    actuary: v.source === 'ACTUARIAL_REPORT' ? 'External Actuary' : 'Manual entry',
    method: 'Projected Unit Credit',
    dbo: Number(v.totalDbo),
    fv: Number(v.plannedAssetsValue),
    plans: 'SEV+GIFT',
    status,
    assumptions: {
      discount: Number(v.discountRate),
      salaryInc: Number(v.salaryGrowthRate),
      // Real v2 assumptions when the actuary supplied them; fall back only when
      // absent (keeps the dashboard consistent with the Disclosure tab — M6).
      turnover: v.turnoverRate != null ? Number(v.turnoverRate) : 0,
      mortality: v.mortalityTable ?? '—',
      retire: v.retirementAge ?? 60,
      fy: String(new Date(v.valuationDate).getUTCFullYear() + 1),
    },
  };
}

// Convert server monthly entries to the prototype's PeriodicRow shape (one
// row per period, displayed as 'YYYY-MM').
function monthlyToPeriodic(rows: readonly EmpBenefitMonthlyEntryListRow[]): PeriodicRow[] {
  return rows.map((r) => {
    const yyyy = r.period.slice(0, 4);
    const mm = r.period.slice(4, 6);
    return {
      id: r.id,
      period: `${yyyy}-${mm}`,
      date: `${yyyy}-${mm}-${lastDayOfMonth(Number(yyyy), Number(mm))}`,
      sc: Number(r.serviceCost),
      ic: Number(r.interestCost),
      jv: r.journalVoucherNo ?? '—',
      journalEntryId: r.journalEntryId ?? null,
      status: r.status === 'POSTED' ? 'posted' : r.status === 'REVERSED' ? 'reversed' : 'pending',
      approvalStatus: r.approvalStatus ?? 'DRAFT',
      currentStep: r.currentStep ?? 0,
    };
  });
}

function lastDayOfMonth(year: number, month: number): string {
  return String(new Date(year, month, 0).getDate()).padStart(2, '0');
}

// Client-side mirror of the backend LPA severance ladder (lpa-severance.util.ts)
// — used ONLY for the live preview in the create-settlement modal. The
// authoritative figures are computed server-side on create.
const LPA_AMENDMENT_2019 = Date.UTC(2019, 4, 5);
const MS_PER_DAY = 86_400_000;

function previewCompletedYears(hire: Date, settle: Date): number {
  let years = settle.getUTCFullYear() - hire.getUTCFullYear();
  const anniv = Date.UTC(hire.getUTCFullYear() + years, hire.getUTCMonth(), hire.getUTCDate());
  if (settle.getTime() < anniv) years -= 1;
  return Math.max(0, years);
}

function previewSeveranceDays(hire: Date, settle: Date): number {
  const days = Math.floor((settle.getTime() - hire.getTime()) / MS_PER_DAY);
  const years = previewCompletedYears(hire, settle);
  const is2019 = settle.getTime() >= LPA_AMENDMENT_2019;
  if (days < 120) return 0;
  if (years < 1) return 30;
  if (years < 3) return 90;
  if (years < 6) return 180;
  if (years < 10) return 240;
  if (years < 20) return 300;
  return is2019 ? 400 : 300;
}

// (wage / 30) × days — mirrors severanceAmount() in the backend util.
function previewSeverance(wage: number, days: number): number {
  return Math.round(((wage / 30) * days + Number.EPSILON) * 100) / 100;
}

function noop(): void {
  /* TODO(phase-3): wire */
}

export const Route = createFileRoute('/empben')({
  component: EmpBenPage,
});

function EmpBenPage() {
  const companyId = useActiveCompanyId();
  const [tab, setTab] = useState<TabKey>('dashboard');
  const [showUpload, setShowUpload] = useState<boolean>(false);
  const [showRecognize, setShowRecognize] = useState<boolean>(false);
  const [showSettle, setShowSettle] = useState<boolean>(false);
  const [year, setYear] = useState<number>(Number(DEMO_YEAR));

  // ----- Server queries -----
  const valuationsQuery = useEmpBenefitValuations(companyId);
  const monthlyQuery = useEmpBenefitMonthly(companyId);
  const settlementsQuery = useEmpBenefitSettlements(companyId);
  const disclosureQuery = useEmpBenefitDisclosure(companyId, year);
  const proposeMutation = useProposeEmpBenefitMonthly(companyId);

  // ----- Derived: actuarial reports list (server-driven; empty state when none) -----
  const reports: readonly ActuaryReport[] = useMemo(() => {
    const rows = valuationsQuery.data ?? [];
    return rows.map((v, i) => valuationToReport(v, i === 0 ? 'current' : 'prior'));
  }, [valuationsQuery.data]);

  const hasValuation = reports.length > 0;
  const curr: ActuaryReport = reports[0] ?? EMPTY_REPORT;
  const currMove: ActuaryMovement = curr.movement ?? {
    open: curr.dbo,
    // Approximation: prior-year SC/IC derived from current assumptions if the
    // server hasn't supplied a movement breakdown.
    sc: Math.round((curr.dbo * curr.assumptions.salaryInc) / 100),
    ic: Math.round((curr.dbo * curr.assumptions.discount) / 100),
    benefit_paid: 0,
    actuarial: 0,
  };

  // ----- Derived: periodic rows (server-driven; empty when none) -----
  const periodic: readonly PeriodicRow[] = useMemo(() => {
    const rows = monthlyQuery.data ?? [];
    return monthlyToPeriodic(rows).reverse(); // server returns desc, UI shows asc
  }, [monthlyQuery.data]);

  const posted = periodic.filter((p) => p.status === 'posted');
  const ytdSC = posted.reduce((s, p) => s + p.sc, 0);
  const ytdIC = posted.reduce((s, p) => s + p.ic, 0);

  // ----- Derived: settlements (server-driven; replaces the EB_RETIREES mock) -----
  const settlements: readonly EmpBenefitSettlementRow[] = useMemo(
    () => settlementsQuery.data ?? [],
    [settlementsQuery.data],
  );
  const settledYtd = settlements.filter(
    (s) => s.status === 'POSTED' && String(s.settlementDate).startsWith(DEMO_YEAR),
  );
  const ytdPaid = settledYtd.reduce((sum, r) => sum + Number(r.severanceAmount), 0);
  // settlementGainLoss = carriedProvision − severance (the "reversal" / gain).
  const ytdReversal = settledYtd.reduce((sum, r) => sum + Number(r.settlementGainLoss), 0);
  const dboCurrent = curr.dbo + ytdSC + ytdIC - ytdPaid - ytdReversal;

  // ----- Latest valuation for KPI "employee count" -----
  const latestValuation: EmpBenefitValuation | null = useMemo(() => {
    const rows = valuationsQuery.data ?? [];
    return rows[0] ?? null;
  }, [valuationsQuery.data]);
  const employeeCount = latestValuation?.employeeCount ?? 0;
  const monthlyAccrual =
    Math.round((curr.dbo * curr.assumptions.salaryInc) / 100 / 12) +
    Math.round((curr.dbo * curr.assumptions.discount) / 100 / 12);

  const handleRunMonth = (): void => {
    if (!companyId) return;
    proposeMutation.mutate(
      { period: DEMO_PERIOD },
      {
        onSuccess: () => {
          setShowRecognize(false);
        },
      },
    );
  };

  // Export the IAS 19 disclosure payload to a single-sheet .xlsx (client-side,
  // matching the other modules' ส่งออก pattern).
  const handleExportDisclosure = (): void => {
    const d = disclosureQuery.data;
    if (!d) return;
    const cur = d.dboReconciliation.current;
    const pri = d.dboReconciliation.prior;
    const a = d.assumptions;
    const aoa: (string | number)[][] = [
      [`หมายเหตุประกอบงบ TAS 19 — ปี ${d.year} (แผน ${d.planCode})`],
      [],
      ['1. การเปลี่ยนแปลงมูลค่าปัจจุบันของภาระผูกพัน', String(d.year), String(d.year - 1)],
      ['มูลค่าปัจจุบัน ณ ต้นงวด', cur.opening, pri.opening],
      ['ต้นทุนบริการปัจจุบัน (SC)', cur.serviceCost, pri.serviceCost],
      ['ต้นทุนดอกเบี้ย (IC)', cur.interestCost, pri.interestCost],
      ['ต้นทุนบริการในอดีต (PSC)', cur.pastServiceCost, pri.pastServiceCost],
      ['ผลกำไร(ขาดทุน)จากการประมาณการ (OCI)', cur.remeasurement, pri.remeasurement],
      ['ผลประโยชน์ที่จ่าย', -cur.benefitsPaid, -pri.benefitsPaid],
      ['มูลค่าปัจจุบัน ณ สิ้นงวด', cur.closing, pri.closing],
      [],
      ['2. สมมติฐานหลัก', String(d.year), String(d.year - 1)],
      ['อัตราคิดลด (%)', a.current.discountRate ?? 0, a.prior.discountRate ?? 0],
      ['อัตราเพิ่มเงินเดือน (%)', a.current.salaryGrowthRate ?? 0, a.prior.salaryGrowthRate ?? 0],
      ['อัตราการลาออก (%)', a.current.turnoverRate ?? 0, a.prior.turnoverRate ?? 0],
      ['อายุเกษียณ', a.current.retirementAge ?? 0, a.prior.retirementAge ?? 0],
      ['ตารางมรณะ', a.current.mortalityTable ?? '—', a.prior.mortalityTable ?? '—'],
      [],
      ['3. ค่าใช้จ่ายที่รับรู้ในกำไรขาดทุน'],
      ['ต้นทุนบริการปัจจุบัน', d.profitOrLoss.currentServiceCost],
      ['ต้นทุนดอกเบี้ย', d.profitOrLoss.interestCost],
      ['ต้นทุนบริการในอดีต', d.profitOrLoss.pastServiceCost],
      ['กำไร(ขาดทุน)จาก Settlement', -d.profitOrLoss.settlementGainLoss],
      ['รวมที่รับรู้ใน P&L', d.profitOrLoss.total],
      [],
      ['4. การวิเคราะห์ความอ่อนไหว (Sensitivity)', '+0.5% / +1 ปี', '-0.5% / -1 ปี'],
      ...(d.sensitivity ?? []).map((s) => [s.label, s.plusImpact, s.minusImpact]),
      [],
      ['5. ประมาณการจ่ายผลประโยชน์ในอนาคต (Maturity Profile)'],
      ...(d.maturityProfile
        ? [
            ['ภายใน 1 ปี', d.maturityProfile.withinOneYear],
            ['1 – 2 ปี', d.maturityProfile.oneToTwoYears],
            ['2 – 5 ปี', d.maturityProfile.twoToFiveYears],
            ['เกิน 5 ปี', d.maturityProfile.beyondFiveYears],
            ['รวม', maturityTotal(d.maturityProfile)],
          ]
        : [['— ไม่มีข้อมูลจาก Actuary —']]),
      ['อายุเฉลี่ยถ่วงน้ำหนัก (ปี)', d.weightedAvgDuration ?? 0],
      [],
      ['การกระทบยอด GL', ''],
      [
        `ยอด GL ภาระผูกพัน (${d.glReconciliation.liabilityAccountCode})`,
        d.glReconciliation.glLiabilityBalance,
      ],
      ['ยอดตาม roll-forward', d.glReconciliation.walkClosing],
      ['ผลต่าง', d.glReconciliation.difference],
      ['ตรงกัน', d.glReconciliation.tiedOut ? 'ใช่' : 'ไม่ตรง — ต้องตรวจสอบ'],
    ];
    if (d.departureNote) aoa.push([], ['หมายเหตุนโยบาย', d.departureNote]);
    void downloadXlsx(`TAS19_Disclosure_${d.year}_${todayStamp()}.xlsx`, aoa, {
      sheetName: `Disclosure ${d.year}`,
      colWidths: [{ wch: 42 }, { wch: 20 }, { wch: 20 }],
    });
  };

  const tabs: readonly (readonly [TabKey, string])[] = [
    ['dashboard', 'Dashboard'],
    ['actuarial', 'รายงาน Actuary'],
    ['periodic', 'รับรู้รายงวด'],
    ['retirement', 'การเกษียณจริง / Settlement'],
    ['disclosure', 'หมายเหตุประกอบงบ'],
  ];

  return (
    <div className="page">
      <PageHead
        crumbs={['บัญชีแยกประเภท', 'TAS 19 — ผลประโยชน์พนักงาน']}
        title="ผลประโยชน์พนักงาน (TAS 19 Employee Benefits)"
        actions={
          <>
            <HelpButton guide="empben" />
            <select
              className="select"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
            >
              <option value={2026}>ปี: 2026</option>
              <option value={2025}>2025</option>
              <option value={2024}>2024</option>
            </select>
            <button className="btn" onClick={() => setShowUpload(true)}>
              <Icon name="upload" size={14} /> Upload Actuarial Report
            </button>
            <button
              className="btn"
              onClick={handleExportDisclosure}
              disabled={!disclosureQuery.data}
            >
              <Icon name="download" size={14} /> Export Disclosure
            </button>
            <button
              className="btn primary"
              onClick={() => setShowRecognize(true)}
              disabled={!companyId || proposeMutation.isPending}
            >
              <Icon name="check" size={14} />{' '}
              {proposeMutation.isPending ? 'กำลังรัน…' : 'รัน TAS 19 ประจำเดือน'}
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            ภาระผูกพัน DBO ปัจจุบัน (20170027)
          </div>
          <div className="value tnum">
            {fmtB(dboCurrent)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">ณ 30 เม.ย. 2026 · จาก Actuary {curr.actuary}</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--brand)' }} />
            จำนวนพนักงานในแผน
          </div>
          <div className="value tnum">
            {fmt(employeeCount)} <span className="unit">คน</span>
          </div>
          <div className="trend faint">
            SC {fmt(ytdSC)} + IC {fmt(ytdIC)}
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--positive)' }} />
            ค่าใช้จ่ายประจำเดือน (SC + IC)
          </div>
          <div className="value tnum">
            {fmt(monthlyAccrual)} <span className="unit">บาท</span>
          </div>
          <div className="trend pos">
            <Icon name="check" size={11} /> รายเดือน · simplified actuarial
          </div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            ค่าใช้จ่าย YTD (P&L)
          </div>
          <div className="value tnum">
            {fmt(ytdSC + ytdIC)} <span className="unit">บาท</span>
          </div>
          <div className="trend down">
            จาก {posted.length} งวดที่รับรู้แล้ว · จ่ายจริง {fmt(ytdPaid)} บาท
          </div>
        </div>
      </div>

      <div
        style={{
          background: '#fafafa',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '14px 18px',
          marginBottom: 'var(--section-gap)',
        }}
      >
        <div className="section-title" style={{ margin: '0 0 10px' }}>
          หลักการ TAS 19 (PAE) — Defined Benefit Plan
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4,1fr)',
            gap: 14,
            fontSize: 14,
          }}
        >
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>① Service Cost (SC)</div>
            <div className="code muted" style={{ fontSize: 13 }}>
              Dr. ค่าใช้จ่าย (60110092)
            </div>
            <div className="code muted" style={{ fontSize: 13 }}>
              &nbsp;&nbsp;&nbsp;Cr. ภาระผูกพัน (20170027)
            </div>
            <div className="meta" style={{ marginTop: 4 }}>
              ต้นทุนบริการในงวด → P&L
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>② Interest Cost (IC)</div>
            <div className="code muted" style={{ fontSize: 13 }}>
              Dr. ดอกเบี้ยจ่าย (60200004)
            </div>
            <div className="code muted" style={{ fontSize: 13 }}>
              &nbsp;&nbsp;&nbsp;Cr. ภาระผูกพัน (20170027)
            </div>
            <div className="meta" style={{ marginTop: 4 }}>
              DBO ต้น × Discount Rate → P&L
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>③ Actuarial G/L</div>
            <div className="code muted" style={{ fontSize: 13 }}>
              Dr/Cr OCI (30200006)
            </div>
            <div className="code muted" style={{ fontSize: 13 }}>
              &nbsp;&nbsp;&nbsp;Cr/Dr ภาระผูกพัน (20170027)
            </div>
            <div className="meta" style={{ marginTop: 4 }}>
              Remeasurement → OCI (ไม่ผ่าน P&L)
            </div>
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>④ Benefit Paid (เกษียณจริง)</div>
            <div className="code muted" style={{ fontSize: 13 }}>
              Dr. ภาระผูกพัน (20170027)
            </div>
            <div className="code muted" style={{ fontSize: 13 }}>
              &nbsp;&nbsp;&nbsp;Cr. เงินฝากธนาคาร (บัญชีหลักของบริษัท)
            </div>
            <div className="meta" style={{ marginTop: 4 }}>
              กลับประมาณการ + จ่ายจริง
            </div>
          </div>
        </div>
      </div>

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {!hasValuation && !valuationsQuery.isPending && (
        <div
          style={{
            padding: '14px 18px',
            background: '#fef3e8',
            border: '1px solid #f0dab0',
            borderLeft: '4px solid var(--warning)',
            borderRadius: 8,
            marginBottom: 'var(--section-gap)',
            fontSize: 14,
            lineHeight: 1.55,
          }}
        >
          ยังไม่มีรายงาน Actuarial Valuation — กดปุ่ม &quot;<b>Upload Actuarial Report</b>&quot;
          เพื่อตั้งค่า DBO และสมมติฐานเริ่มต้น
        </div>
      )}

      {tab === 'dashboard' && (
        <EBDashboard
          dbo={dboCurrent}
          ytdSC={ytdSC}
          ytdIC={ytdIC}
          ytdPaid={ytdPaid}
          settlementReversal={ytdReversal}
          curr={curr}
          currMove={currMove}
        />
      )}
      {tab === 'actuarial' && (
        <EBActuarial
          companyId={companyId}
          reports={reports}
          onUpload={() => setShowUpload(true)}
          isPending={valuationsQuery.isPending}
          error={valuationsQuery.error}
        />
      )}
      {tab === 'periodic' && (
        <EBPeriodic
          companyId={companyId}
          periodic={periodic}
          curr={curr}
          currMove={currMove}
          onRecognize={() => setShowRecognize(true)}
          isPending={monthlyQuery.isPending}
          error={monthlyQuery.error}
        />
      )}
      {tab === 'retirement' && (
        <EBRetirement
          companyId={companyId}
          settlements={settlements}
          onCreate={() => setShowSettle(true)}
          isPending={settlementsQuery.isPending}
          error={settlementsQuery.error}
        />
      )}
      {tab === 'disclosure' && (
        <EBDisclosure
          disclosure={disclosureQuery.data}
          isPending={disclosureQuery.isPending}
          companyId={companyId}
          valuationId={latestValuation?.id}
        />
      )}

      {showUpload && (
        <UploadReportModal companyId={companyId} onClose={() => setShowUpload(false)} />
      )}
      {showRecognize && (
        <RecognizeModal
          curr={curr}
          currMove={currMove}
          isPending={proposeMutation.isPending}
          onConfirm={handleRunMonth}
          onClose={() => setShowRecognize(false)}
        />
      )}
      {showSettle && <SettleModal companyId={companyId} onClose={() => setShowSettle(false)} />}
    </div>
  );
}

interface EBDashboardProps {
  dbo: number;
  ytdSC: number;
  ytdIC: number;
  ytdPaid: number;
  settlementReversal: number;
  curr: ActuaryReport;
  currMove: ActuaryMovement;
}

interface FlowRow {
  label: string;
  amt: number;
  type: 'open' | 'add' | 'sub';
  acct: string;
}

function EBDashboard({
  dbo,
  ytdSC,
  ytdIC,
  ytdPaid,
  settlementReversal,
  curr,
  currMove,
}: EBDashboardProps) {
  const open = currMove.open;
  const reversal = settlementReversal;
  const flow: readonly FlowRow[] = [
    { label: 'ยอดยกมาต้นปี (1 ม.ค. 2026)', amt: open, type: 'open', acct: '20170027' },
    { label: 'บวก: Current Service Cost (YTD)', amt: ytdSC, type: 'add', acct: '60110092' },
    { label: 'บวก: Interest Cost (YTD)', amt: ytdIC, type: 'add', acct: '60200004' },
    { label: 'หัก: Benefit Paid (เกษียณจริง)', amt: -ytdPaid, type: 'sub', acct: '20170027' },
    {
      label: 'หัก: กลับประมาณการส่วนเกิน',
      amt: -reversal,
      type: 'sub',
      acct: '60110092',
    },
  ];

  return (
    <div className="grid-2">
      <div className="card">
        <div className="card-head">
          <h3>การเคลื่อนไหวของ DBO — YTD พ.ค. 2026</h3>
          <span className="meta">จากรายงาน Actuary {curr.id}</span>
        </div>
        <div className="card-body flush">
          <table className="tbl">
            <thead>
              <tr>
                <th>รายการ</th>
                <th>บัญชี</th>
                <th className="num">จำนวน</th>
              </tr>
            </thead>
            <tbody>
              {flow.map((f, i) => (
                <tr
                  key={i}
                  style={f.type === 'open' ? { fontWeight: 600, background: '#fafafa' } : undefined}
                >
                  <td>{f.label}</td>
                  <td className="code muted">{f.acct}</td>
                  <td className={`num tnum ${f.amt < 0 ? 'neg' : ''}`}>{fmt(f.amt)}</td>
                </tr>
              ))}
              <tr className="total">
                <td colSpan={2}>ยอดคงเหลือ ณ ปัจจุบัน</td>
                <td className="num tnum" style={{ color: 'var(--brand)' }}>
                  {fmt(dbo)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h3>สมมติฐานของ Actuary ({curr.actuary})</h3>
          <span className="meta">รายงาน {curr.id}</span>
        </div>
        <div className="card-body">
          <table
            className="tbl"
            style={{ border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}
          >
            <tbody>
              <tr>
                <td>วิธีการคำนวณ</td>
                <td>
                  <b>{curr.method}</b>
                </td>
              </tr>
              <tr>
                <td>อัตราคิดลด (Discount Rate)</td>
                <td className="num">
                  <b>{curr.assumptions.discount.toFixed(2)}%</b>{' '}
                  <span className="meta">ต่อปี · TGB 10Y</span>
                </td>
              </tr>
              <tr>
                <td>อัตราเพิ่มเงินเดือน</td>
                <td className="num">
                  <b>{curr.assumptions.salaryInc.toFixed(2)}%</b>{' '}
                  <span className="meta">ต่อปี</span>
                </td>
              </tr>
              <tr>
                <td>อัตราการลาออก (Turnover)</td>
                <td className="num">
                  <b>{curr.assumptions.turnover.toFixed(2)}%</b>{' '}
                  <span className="meta">ต่อปี (ค่าเฉลี่ย)</span>
                </td>
              </tr>
              <tr>
                <td>ตารางอัตรามรณะ</td>
                <td>
                  <b>{curr.assumptions.mortality}</b>{' '}
                  <span className="meta">(Thai Mortality Table 2022)</span>
                </td>
              </tr>
              <tr>
                <td>อายุเกษียณ</td>
                <td>
                  <b>{curr.assumptions.retire} ปี</b>
                </td>
              </tr>
              <tr>
                <td>เกณฑ์ค่าชดเชย</td>
                <td>
                  สูงสุด <b>400 วัน</b> ของเงินเดือนสุดท้าย
                </td>
              </tr>
            </tbody>
          </table>
          <div
            style={{
              marginTop: 14,
              padding: '12px 14px',
              background: '#e8eff8',
              borderRadius: 6,
              fontSize: 13.5,
              lineHeight: 1.55,
            }}
          >
            <b>หมายเหตุ:</b> สมมติฐานทั้งหมดมาจาก {curr.actuary} รายงานวันที่ {fmtDate(curr.date)}{' '}
            ใช้สำหรับลงบัญชีปี {curr.assumptions.fy} ทั้งปี · ต้อง Re-valuation ทุก 12 เดือน
            หรือเมื่อมีเหตุการณ์ Significant Plan Change
          </div>
        </div>
      </div>

      <div className="card" style={{ gridColumn: '1 / -1' }}>
        <div className="card-head">
          <h3>ผลกระทบต่องบการเงิน</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 24 }}>
            <div>
              <div className="section-title" style={{ margin: '0 0 8px' }}>
                งบกำไรขาดทุน (P&L)
              </div>
              <table className="tbl" style={{ border: '1px solid var(--border)' }}>
                <tbody>
                  <tr>
                    <td>60110092 ค่าใช้จ่ายผลประโยชน์พนักงาน — SC</td>
                    <td className="num tnum">{fmt(ytdSC)}</td>
                  </tr>
                  <tr>
                    <td>60200004 ดอกเบี้ยจ่าย — IC</td>
                    <td className="num tnum">{fmt(ytdIC)}</td>
                  </tr>
                  <tr className="total">
                    <td>รวม</td>
                    <td className="num tnum">{fmt(ytdSC + ytdIC)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div>
              <div className="section-title" style={{ margin: '0 0 8px' }}>
                งบกำไรขาดทุนเบ็ดเสร็จอื่น (OCI)
              </div>
              <table className="tbl" style={{ border: '1px solid var(--border)' }}>
                <tbody>
                  <tr>
                    <td>30200006 Remeasurement — Actuarial Loss</td>
                    <td className="num tnum">—</td>
                  </tr>
                  <tr className="total">
                    <td>รวม</td>
                    <td className="num tnum">—</td>
                  </tr>
                </tbody>
              </table>
              <div className="meta" style={{ marginTop: 8 }}>
                จะรับรู้เมื่อ Actuary ออกรายงานใหม่ปลายปี
              </div>
            </div>
            <div>
              <div className="section-title" style={{ margin: '0 0 8px' }}>
                งบแสดงฐานะการเงิน
              </div>
              <table className="tbl" style={{ border: '1px solid var(--border)' }}>
                <tbody>
                  <tr>
                    <td>20170027 ภาระผูกพันผลประโยชน์พนักงาน</td>
                    <td className="num tnum">
                      <b>{fmt(dbo)}</b>
                    </td>
                  </tr>
                  <tr>
                    <td className="muted">(หนี้สินไม่หมุนเวียน)</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

interface EBActuarialProps {
  companyId: string | undefined;
  reports: readonly ActuaryReport[];
  onUpload: () => void;
  isPending: boolean;
  error: Error | null;
}

export function EBActuarial({ companyId, reports, onUpload, isPending, error }: EBActuarialProps) {
  // Fetch a short-lived presigned URL for the bound report then open it. The
  // button is only enabled when a PDF is actually attached.
  const handleDownloadPdf = (valuationId: string): void => {
    if (!companyId || !valuationId) return;
    void getEmpBenefitValuationPdfUrl(companyId, valuationId).then((res) => {
      window.open(res.url, '_blank', 'noopener');
    });
  };
  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>ทะเบียนรายงาน Actuarial Valuation</span>
        <div style={{ marginLeft: 'auto' }}>
          <button className="btn sm primary" onClick={onUpload}>
            <Icon name="upload" size={12} /> Upload รายงานใหม่
          </button>
        </div>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>เลขที่รายงาน</th>
            <th>ณ วันที่</th>
            <th>Actuary</th>
            <th>วิธีการคำนวณ</th>
            <th className="num">Discount</th>
            <th className="num">Salary Inc</th>
            <th className="num">Turnover</th>
            <th className="num">DBO ที่คำนวณได้</th>
            <th>แผนที่ครอบคลุม</th>
            <th>สถานะ</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {isPending ? (
            <tr>
              <td colSpan={11} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                กำลังโหลด…
              </td>
            </tr>
          ) : error ? (
            <tr>
              <td
                colSpan={11}
                className="muted"
                style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
              >
                โหลดล้มเหลว: {error.message}
              </td>
            </tr>
          ) : reports.length === 0 ? (
            <tr>
              <td colSpan={11} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                ยังไม่มีรายงาน Actuarial — กดปุ่ม &quot;<b>+ Upload รายงานใหม่</b>&quot;
                เพื่อสร้างรายการแรก
              </td>
            </tr>
          ) : (
            reports.map((r) => (
              <tr key={r.id}>
                <td className="code">
                  <b>{r.id}</b>
                </td>
                <td>{fmtDate(r.date)}</td>
                <td>
                  <b>{r.actuary}</b>
                </td>
                <td className="muted">{r.method}</td>
                <td className="num">
                  <b>{r.assumptions.discount.toFixed(2)}%</b>
                </td>
                <td className="num">
                  <b>{r.assumptions.salaryInc.toFixed(2)}%</b>
                </td>
                <td className="num">
                  <b>{r.assumptions.turnover.toFixed(2)}%</b>
                </td>
                <td className="num tnum">
                  <b>{fmt(r.dbo)}</b>
                </td>
                <td>{r.plans}</td>
                <td>
                  {r.status === 'current' ? (
                    <span className="badge brand">
                      <span className="bdot" />
                      ใช้งานปัจจุบัน
                    </span>
                  ) : (
                    <span className="badge neutral">เก็บประวัติ</span>
                  )}
                </td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      className="btn sm ghost"
                      style={{ height: 26, fontSize: 13 }}
                      disabled={!r.pdfStorageKey || !companyId}
                      title={r.pdfStorageKey ? 'เปิด / ดาวน์โหลด PDF' : 'ยังไม่มีไฟล์แนบ'}
                      onClick={() => handleDownloadPdf(r.valuationId)}
                    >
                      ดู PDF
                    </button>
                    <button
                      className="btn sm ghost"
                      style={{ height: 26, fontSize: 13 }}
                      onClick={noop}
                    >
                      เปรียบเทียบ
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      <div
        style={{
          padding: '12px 14px',
          background: '#fafafa',
          fontSize: 14,
          color: 'var(--text-muted)',
          lineHeight: 1.55,
          borderTop: '1px solid var(--border)',
        }}
      >
        <b>ข้อกำหนด:</b> บริษัท PAE ต้องทำ Actuarial Valuation ทุก 12 เดือน · WTW / Milliman / Aon
        เป็น Actuary ที่นิยมในไทย · รายงานต้องลงนามรับรองโดย Fellow of Actuary Society
      </div>
    </div>
  );
}

interface EBPeriodicProps {
  companyId: string | undefined;
  periodic: readonly PeriodicRow[];
  curr: ActuaryReport;
  currMove: ActuaryMovement;
  onRecognize: () => void;
  isPending: boolean;
  error: Error | null;
}

export function EBPeriodic({
  companyId,
  periodic,
  curr,
  currMove,
  onRecognize,
  isPending,
  error,
}: EBPeriodicProps) {
  const total = (sc: number, ic: number): number => sc + ic;
  const posted = periodic.filter((p) => p.status === 'posted');
  const monthlyService = Math.round(currMove.sc / 12);
  const monthlyInterest = Math.round(currMove.ic / 12);
  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          การรับรู้ค่าใช้จ่ายผลประโยชน์พนักงานรายงวด — ปี 2026
        </span>
        <div style={{ marginLeft: 'auto' }}>
          <button className="btn sm primary" onClick={onRecognize}>
            <Icon name="check" size={12} /> รัน TAS 19 ประจำเดือน
          </button>
        </div>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>งวด</th>
            <th>วันที่ลงบัญชี</th>
            <th className="num">Service Cost (SC)</th>
            <th className="num">Interest Cost (IC)</th>
            <th className="num">รวมค่าใช้จ่าย</th>
            <th>เลขที่ JV</th>
            <th>ผู้รับรู้</th>
            <th>สถานะ</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {isPending ? (
            <tr>
              <td colSpan={9} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                กำลังโหลด…
              </td>
            </tr>
          ) : error ? (
            <tr>
              <td
                colSpan={9}
                className="muted"
                style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
              >
                โหลดล้มเหลว: {error.message}
              </td>
            </tr>
          ) : periodic.length === 0 ? (
            <tr>
              <td colSpan={9} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                ยังไม่มีงวดที่รับรู้ — กดปุ่ม &quot;<b>รัน TAS 19 ประจำเดือน</b>&quot; เพื่อสร้าง JV
                งวดแรก
              </td>
            </tr>
          ) : (
            periodic.map((p) => (
              <tr key={p.period}>
                <td>
                  <b>{p.period}</b>
                </td>
                <td>{fmtDate(p.date)}</td>
                <td className="num tnum">{fmt(p.sc)}</td>
                <td className="num tnum" style={{ color: 'var(--info)' }}>
                  {fmt(p.ic)}
                </td>
                <td className="num tnum">
                  <b>{fmt(total(p.sc, p.ic))}</b>
                </td>
                <td className="code">
                  {p.jv === '—' ? (
                    <span className="faint">—</span>
                  ) : (
                    <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{p.jv}</a>
                  )}
                </td>
                <td className="muted">{p.status === 'posted' ? 'system' : '—'}</td>
                <td>
                  {p.status === 'posted' ? (
                    <span className="badge positive">
                      <Icon name="check" size={10} /> รับรู้แล้ว
                    </span>
                  ) : p.status === 'reversed' ? (
                    <span className="badge info">
                      <span className="bdot" />
                      กลับรายการแล้ว
                    </span>
                  ) : (
                    <span className="badge warning">
                      <span className="bdot" />
                      รอรับรู้
                    </span>
                  )}
                </td>
                <td>
                  {p.status === 'reversed' ? (
                    <span className="meta">—</span>
                  ) : (
                    <EmpBenefitApprovalActions
                      companyId={companyId}
                      kind="monthly"
                      id={p.id}
                      approvalStatus={p.approvalStatus}
                      currentStep={p.currentStep}
                      posted={p.status === 'posted'}
                      label={p.period}
                      submitPermission="empben.monthly.run"
                      defaultReversalPeriod={p.period.replace('-', '')}
                      journalEntryId={p.journalEntryId}
                    />
                  )}
                </td>
              </tr>
            ))
          )}
          <tr className="total">
            <td colSpan={2}>รวม YTD</td>
            <td className="num tnum">{fmt(posted.reduce((s, p) => s + p.sc, 0))}</td>
            <td className="num tnum">{fmt(posted.reduce((s, p) => s + p.ic, 0))}</td>
            <td className="num tnum">{fmt(posted.reduce((s, p) => s + p.sc + p.ic, 0))}</td>
            <td colSpan={4} />
          </tr>
        </tbody>
      </table>
      <div
        style={{
          padding: '14px 18px',
          background: '#fafafa',
          borderTop: '1px solid var(--border)',
          fontSize: 14,
          lineHeight: 1.55,
        }}
      >
        <b>การคำนวณรายเดือน (simplified):</b> SC ต่อเดือน = DBO ({fmt(curr.dbo)}) ×{' '}
        {curr.assumptions.salaryInc.toFixed(2)}% ÷ 12 ={' '}
        <b className="tnum">
          {fmt(Math.round((curr.dbo * curr.assumptions.salaryInc) / 100 / 12))}
        </b>{' '}
        · IC ต่อเดือน = DBO ต้น ({fmt(currMove.open)}) × {curr.assumptions.discount.toFixed(2)}% ÷
        12 = <b className="tnum">{fmt(monthlyInterest)}</b>
        <div className="meta" style={{ marginTop: 4 }}>
          (สูตรประมาณ — สำหรับ PV จริงต้องใช้ Projected Unit Credit จาก Actuary; ดู open question ใน
          handover) · monthlyService prototype reference ={' '}
          <b className="tnum">{fmt(monthlyService)}</b>
        </div>
      </div>
    </div>
  );
}

// Reverse a posted doc — books the contra-JV into a chosen OPEN period (YYYYMM).
function ReversePeriodModal({
  label,
  defaultPeriod,
  busy,
  error,
  onConfirm,
  onClose,
}: {
  label: string;
  defaultPeriod: string;
  busy: boolean;
  error: string | null;
  onConfirm: (period: string) => void;
  onClose: () => void;
}) {
  const [period, setPeriod] = useState<string>(defaultPeriod);
  const valid = /^\d{6}$/.test(period);
  return (
    <AppModal
      title={`กลับรายการ (Reverse) — ${label}`}
      subtitle="ลง JV กลับด้านเข้างวดที่เปิดอยู่ (ไม่แก้ voucher เดิม)"
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={busy || !valid}
            onClick={() => onConfirm(period)}
          >
            {busy ? 'กำลังกลับรายการ…' : 'ยืนยันกลับรายการ'}
          </button>
        </>
      }
    >
      <label className="meta" style={{ display: 'block', marginBottom: 6 }}>
        งวดที่จะลงรายการกลับ (YYYYMM) *
      </label>
      <input
        className="input"
        value={period}
        onChange={(e) => setPeriod(e.target.value)}
        placeholder="202606"
        style={{ width: '100%', height: 38, padding: 8 }}
      />
      {error && <div style={{ color: 'var(--negative)', fontSize: 14, marginTop: 8 }}>{error}</div>}
    </AppModal>
  );
}

// Central approval-engine action cell — submit a DRAFT, พิจารณา (approve/reject/
// return) a PENDING doc, or ดู JV / กลับรายการ once POSTED. Shared by the
// settlement register and the monthly accrual table.
interface ApprovalActionsProps {
  companyId: string | undefined;
  kind: EmpBenefitDocKind;
  id: string;
  approvalStatus: EmpBenefitApprovalStatus;
  currentStep: number;
  posted: boolean;
  label: string;
  submitPermission: string;
  defaultReversalPeriod: string;
  journalEntryId: string | null;
}

function EmpBenefitApprovalActions({
  companyId,
  kind,
  id,
  approvalStatus,
  currentStep,
  posted,
  label,
  submitPermission,
  defaultReversalPeriod,
  journalEntryId,
}: ApprovalActionsProps) {
  const navigate = useNavigate();
  const submitMut = useEmpBenefitSubmit(companyId, kind);
  const decideMut = useEmpBenefitDecide(companyId, kind);
  const reverseMut = useEmpBenefitReverse(companyId, kind);
  const [showDecide, setShowDecide] = useState<boolean>(false);
  const [showReverse, setShowReverse] = useState<boolean>(false);
  const [err, setErr] = useState<string | null>(null);
  const busy = submitMut.isPending || decideMut.isPending;

  const handleDecide = (d: Decision): void => {
    setErr(null);
    const input =
      d.action === 'approve'
        ? { id, action: 'approve' as const, step: d.step, comment: d.comment }
        : { id, action: d.action, step: d.step, reason: d.reason };
    decideMut.mutate(input, {
      onSuccess: () => setShowDecide(false),
      onError: (e: unknown) => setErr(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'),
    });
  };

  if (posted) {
    return (
      <div style={{ display: 'flex', gap: 6 }}>
        <button
          className="btn sm ghost"
          style={{ height: 28, fontSize: 14 }}
          disabled={!journalEntryId}
          title={journalEntryId ? 'เปิดใบสำคัญใน /journal' : 'ยังไม่มีใบสำคัญ'}
          onClick={() => {
            if (journalEntryId) void navigate({ to: '/journal', search: { jv: journalEntryId } });
          }}
        >
          ดู JV
        </button>
        <Can permission="empben.reverse">
          <button
            className="btn sm ghost"
            style={{ height: 28, fontSize: 14 }}
            disabled={reverseMut.isPending}
            onClick={() => setShowReverse(true)}
          >
            กลับรายการ
          </button>
        </Can>
        {showReverse && (
          <ReversePeriodModal
            label={label}
            defaultPeriod={defaultReversalPeriod}
            busy={reverseMut.isPending}
            error={err}
            onConfirm={(period) => {
              setErr(null);
              reverseMut.mutate(
                { id, reversalPeriod: period },
                {
                  onSuccess: () => setShowReverse(false),
                  onError: (e: unknown) =>
                    setErr(e instanceof Error ? e.message : 'เกิดข้อผิดพลาด'),
                },
              );
            }}
            onClose={() => {
              setShowReverse(false);
              setErr(null);
            }}
          />
        )}
      </div>
    );
  }
  if (approvalStatus === 'DRAFT' || approvalStatus === 'RETURNED') {
    return (
      <Can permission={submitPermission}>
        <button
          className="btn sm primary"
          style={{ height: 28, fontSize: 14 }}
          disabled={busy}
          onClick={() => submitMut.mutate(id)}
        >
          {submitMut.isPending ? 'กำลังส่ง…' : 'ส่งอนุมัติ'}
        </button>
      </Can>
    );
  }
  if (approvalStatus === 'PENDING') {
    return (
      <>
        <Can permission="empben.approve">
          <button
            className="btn sm primary"
            style={{ height: 28, fontSize: 14 }}
            disabled={busy}
            onClick={() => setShowDecide(true)}
          >
            พิจารณา (ขั้น {currentStep})
          </button>
        </Can>
        {showDecide && (
          <DecisionModal
            voucherNo={label}
            step={currentStep || 1}
            busy={decideMut.isPending}
            error={err}
            titlePrefix="พิจารณา TAS 19"
            onConfirm={handleDecide}
            onClose={() => {
              setShowDecide(false);
              setErr(null);
            }}
          />
        )}
      </>
    );
  }
  return <span className="meta">{approvalStatus}</span>;
}

interface EBRetirementProps {
  companyId: string | undefined;
  settlements: readonly EmpBenefitSettlementRow[];
  onCreate: () => void;
  isPending: boolean;
  error: unknown;
}

export function EBRetirement({
  companyId,
  settlements,
  onCreate,
  isPending,
  error,
}: EBRetirementProps) {
  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          ทะเบียนการชำระผลประโยชน์ (Settlement) & การกลับประมาณการ
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select className="select">
            <option>สถานะ: ทั้งหมด</option>
            <option>จ่ายเงินแล้ว</option>
            <option>รอจ่าย</option>
            <option>ใกล้เกษียณ</option>
          </select>
          <button
            className="btn sm primary"
            style={{ height: 30, fontSize: 14 }}
            onClick={onCreate}
          >
            <Icon name="plus" size={13} /> บันทึก settlement
          </button>
        </div>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>พนักงาน</th>
            <th>แผนก / ตำแหน่ง</th>
            <th>วันเริ่ม → วันเกษียณ</th>
            <th className="num">อายุงาน</th>
            <th className="num">เงินเดือนสุดท้าย</th>
            <th className="num">ค่าชดเชย (LPA)</th>
            <th className="num">Provision สะสม</th>
            <th className="num">ส่วนต่าง</th>
            <th>สถานะ</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {settlements.map((s) => {
            const gainLoss = Number(s.settlementGainLoss);
            const display = settlementDisplayStatus(s);
            return (
              <tr key={s.id}>
                <td>
                  <b>{s.employeeName}</b>
                  <div className="meta">{s.employeeRef}</div>
                </td>
                <td>
                  {s.position ?? '—'}
                  <div className="meta">{s.department ?? '—'}</div>
                </td>
                <td className="muted">
                  {fmtDate(s.hireDate)} → {fmtDate(s.settlementDate)}
                </td>
                <td className="num">
                  <b>{s.completedYears} ปี</b>
                </td>
                <td className="num tnum">{fmt(Number(s.finalMonthlyWage))}</td>
                <td className="num tnum">
                  {fmt(Number(s.severanceAmount))}
                  <div className="meta">{s.severanceDays} วัน</div>
                </td>
                <td className="num tnum muted">{fmt(Number(s.carriedProvision))}</td>
                <td className={`num tnum ${gainLoss > 0 ? 'pos' : gainLoss < 0 ? 'neg' : ''}`}>
                  <b>
                    {gainLoss > 0 ? '+' : ''}
                    {fmt(gainLoss)}
                  </b>
                </td>
                <td>
                  {s.status === 'REVERSED' ? (
                    <span className="badge info">
                      <span className="bdot" /> กลับรายการแล้ว
                    </span>
                  ) : display === 'settled' ? (
                    <span className="badge positive">
                      <Icon name="check" size={10} /> จ่ายเงินแล้ว
                    </span>
                  ) : display === 'pending' ? (
                    <span className="badge warning">
                      <span className="bdot" /> รอจ่าย
                    </span>
                  ) : (
                    <span className="badge info">
                      <span className="bdot" /> ใกล้เกษียณ
                    </span>
                  )}
                </td>
                <td>
                  <EmpBenefitApprovalActions
                    companyId={companyId}
                    kind="settlements"
                    id={s.id}
                    approvalStatus={s.approvalStatus}
                    currentStep={s.currentStep}
                    posted={s.status === 'POSTED'}
                    label={s.employeeName}
                    submitPermission="empben.settlement.create"
                    defaultReversalPeriod={DEMO_PERIOD}
                    journalEntryId={s.journalEntryId ?? null}
                  />
                </td>
              </tr>
            );
          })}
          {!settlements.length && (
            <tr>
              <td
                colSpan={10}
                style={{ textAlign: 'center', padding: '28px 0', color: 'var(--text-muted)' }}
              >
                {isPending
                  ? 'กำลังโหลด…'
                  : error
                    ? 'โหลดข้อมูลไม่สำเร็จ'
                    : 'ยังไม่มีรายการ settlement — กด "บันทึก settlement" เพื่อเริ่ม'}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      <div
        style={{
          padding: '14px 18px',
          background: '#fafafa',
          borderTop: '1px solid var(--border)',
          fontSize: 14,
          lineHeight: 1.55,
        }}
      >
        <b>การกลับประมาณการ:</b> เมื่อพนักงานเกษียณจริง บริษัทจ่ายค่าชดเชยตาม พ.ร.บ.คุ้มครองแรงงาน
        (LPA ladder สูงสุด 400 วัน) — ส่วนต่างระหว่าง Provision สะสมกับ ค่าชดเชยที่จ่ายจริงคือ{' '}
        <b>settlement gain/loss</b> · ตามนโยบายบริษัท (ALL_TO_PL) รับรู้เข้า P&L ทั้งจำนวน
        (เบี่ยงเบนจาก IAS 19.111 โดยเจตนา)
      </div>
    </div>
  );
}

interface EBDisclosureProps {
  disclosure: EmpBenefitDisclosure | undefined;
  isPending: boolean;
  companyId: string | undefined;
  valuationId: string | undefined;
}

export function EBDisclosure({ disclosure, isPending, companyId, valuationId }: EBDisclosureProps) {
  if (!disclosure) {
    return (
      <div className="card">
        <div className="card-body" style={{ textAlign: 'center', color: 'var(--text-muted)' }}>
          {isPending ? 'กำลังโหลดหมายเหตุประกอบงบ…' : 'ยังไม่มีข้อมูล disclosure สำหรับปีนี้'}
        </div>
      </div>
    );
  }

  const cur = disclosure.dboReconciliation.current;
  const pri = disclosure.dboReconciliation.prior;
  const a = disclosure.assumptions;
  const pl = disclosure.profitOrLoss;
  const yr = disclosure.year;
  const pct = (v: number | null): string => (v == null ? '—' : `${v.toFixed(2)}%`);
  const dn = (v: number): string => (v === 0 ? '—' : fmt(v));
  const impactCell = (v: number) => (
    <td
      className="num tnum"
      style={{ color: v < 0 ? 'var(--positive)' : v > 0 ? 'var(--negative)' : undefined }}
    >
      {v > 0 ? '+' : ''}
      {fmt(v)}
    </td>
  );

  return (
    <>
      <div className="grid-2-eq">
        <div className="card">
          <div className="card-head">
            <h3>1. การเปลี่ยนแปลงในมูลค่าปัจจุบันของภาระผูกพัน</h3>
          </div>
          <div className="card-body flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>รายการ</th>
                  <th className="num">{yr}</th>
                  <th className="num">{yr - 1}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>มูลค่าปัจจุบัน ณ ต้นงวด</td>
                  <td className="num tnum">{fmt(cur.opening)}</td>
                  <td className="num tnum">{fmt(pri.opening)}</td>
                </tr>
                <tr>
                  <td className="indent-1">ต้นทุนบริการในงวดปัจจุบัน</td>
                  <td className="num tnum">{fmt(cur.serviceCost)}</td>
                  <td className="num tnum">{fmt(pri.serviceCost)}</td>
                </tr>
                <tr>
                  <td className="indent-1">ต้นทุนดอกเบี้ย</td>
                  <td className="num tnum">{fmt(cur.interestCost)}</td>
                  <td className="num tnum">{fmt(pri.interestCost)}</td>
                </tr>
                <tr>
                  <td className="indent-1">ต้นทุนบริการในอดีต (PSC)</td>
                  <td className="num tnum">{dn(cur.pastServiceCost)}</td>
                  <td className="num tnum">{dn(pri.pastServiceCost)}</td>
                </tr>
                <tr>
                  <td className="indent-1">ผลกำไร (ขาดทุน) จากการประมาณการ → OCI</td>
                  <td className="num tnum">{dn(cur.remeasurement)}</td>
                  <td className="num tnum">{dn(pri.remeasurement)}</td>
                </tr>
                <tr>
                  <td className="indent-1">ผลประโยชน์ที่จ่ายให้พนักงาน</td>
                  <td className="num tnum">({fmt(cur.benefitsPaid)})</td>
                  <td className="num tnum">({fmt(pri.benefitsPaid)})</td>
                </tr>
                <tr className="total">
                  <td>มูลค่าปัจจุบัน ณ สิ้นงวด</td>
                  <td className="num tnum">{fmt(cur.closing)}</td>
                  <td className="num tnum">{fmt(pri.closing)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>2. สมมติฐานหลักในการคำนวณ</h3>
          </div>
          <div className="card-body flush">
            <table className="tbl">
              <thead>
                <tr>
                  <th>สมมติฐาน</th>
                  <th className="num">{yr}</th>
                  <th className="num">{yr - 1}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>อัตราคิดลด (Discount Rate)</td>
                  <td className="num">{pct(a.current.discountRate)}</td>
                  <td className="num">{pct(a.prior.discountRate)}</td>
                </tr>
                <tr>
                  <td>อัตราเพิ่มเงินเดือนในอนาคต</td>
                  <td className="num">{pct(a.current.salaryGrowthRate)}</td>
                  <td className="num">{pct(a.prior.salaryGrowthRate)}</td>
                </tr>
                <tr>
                  <td>อัตราการลาออก (เฉลี่ย)</td>
                  <td className="num">{pct(a.current.turnoverRate)}</td>
                  <td className="num">{pct(a.prior.turnoverRate)}</td>
                </tr>
                <tr>
                  <td>อายุเกษียณ</td>
                  <td className="num">{a.current.retirementAge ?? '—'} ปี</td>
                  <td className="num">{a.prior.retirementAge ?? '—'} ปี</td>
                </tr>
                <tr>
                  <td>ตารางมรณะ</td>
                  <td className="num">{a.current.mortalityTable ?? '—'}</td>
                  <td className="num">{a.prior.mortalityTable ?? '—'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>3. ค่าใช้จ่ายที่รับรู้ในกำไรหรือขาดทุน</h3>
          </div>
          <div className="card-body flush">
            <table className="tbl">
              <tbody>
                <tr>
                  <td>ต้นทุนบริการในงวดปัจจุบัน</td>
                  <td className="num tnum">{fmt(pl.currentServiceCost)}</td>
                </tr>
                <tr>
                  <td>ต้นทุนดอกเบี้ย</td>
                  <td className="num tnum">{fmt(pl.interestCost)}</td>
                </tr>
                <tr>
                  <td>ต้นทุนบริการในอดีต</td>
                  <td className="num tnum">{dn(pl.pastServiceCost)}</td>
                </tr>
                <tr>
                  <td>กำไร (ขาดทุน) จาก Settlement → P&amp;L</td>
                  <td className="num tnum">
                    {pl.settlementGainLoss > 0
                      ? `(${fmt(pl.settlementGainLoss)})`
                      : dn(-pl.settlementGainLoss)}
                  </td>
                </tr>
                <tr className="total">
                  <td>รวมที่รับรู้ใน P&L</td>
                  <td className="num tnum">{fmt(pl.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h3>4. การวิเคราะห์ความอ่อนไหว (Sensitivity)</h3>
          </div>
          <div className="card-body flush">
            {disclosure.sensitivity?.length ? (
              <table className="tbl">
                <thead>
                  <tr>
                    <th>สมมติฐาน</th>
                    <th className="num">+0.5% / +1 ปี</th>
                    <th className="num">-0.5% / -1 ปี</th>
                  </tr>
                </thead>
                <tbody>
                  {disclosure.sensitivity.map((s) => (
                    <tr key={s.assumption}>
                      <td>{s.label}</td>
                      {impactCell(s.plusImpact)}
                      {impactCell(s.minusImpact)}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ padding: '18px', color: 'var(--text-muted)', fontSize: 14 }}>
                ยังไม่มีข้อมูล sensitivity จาก Actuary — ใช้เครื่องมือ What-if
                ด้านล่างเพื่อประเมินคร่าว ๆ
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginTop: 'var(--section-gap)' }}>
        <div className="card-head">
          <h3>5. ประมาณการจ่ายผลประโยชน์ในอนาคต (Maturity Profile)</h3>
          <span className="meta">IAS 19.147(c)</span>
        </div>
        <div className="card-body flush">
          {disclosure.maturityProfile ? (
            <table className="tbl">
              <thead>
                <tr>
                  <th>ช่วงเวลา</th>
                  <th className="num">ประมาณการจ่าย (บาท)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>ภายใน 1 ปี</td>
                  <td className="num tnum">{fmt(disclosure.maturityProfile.withinOneYear)}</td>
                </tr>
                <tr>
                  <td>1 – 2 ปี</td>
                  <td className="num tnum">{fmt(disclosure.maturityProfile.oneToTwoYears)}</td>
                </tr>
                <tr>
                  <td>2 – 5 ปี</td>
                  <td className="num tnum">{fmt(disclosure.maturityProfile.twoToFiveYears)}</td>
                </tr>
                <tr>
                  <td>เกิน 5 ปี</td>
                  <td className="num tnum">{fmt(disclosure.maturityProfile.beyondFiveYears)}</td>
                </tr>
                <tr className="total">
                  <td>รวม</td>
                  <td className="num tnum">{fmt(maturityTotal(disclosure.maturityProfile))}</td>
                </tr>
                {disclosure.weightedAvgDuration != null && (
                  <tr>
                    <td>อายุเฉลี่ยถ่วงน้ำหนักของภาระผูกพัน (ปี)</td>
                    <td className="num tnum">{disclosure.weightedAvgDuration.toFixed(1)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: '18px', color: 'var(--text-muted)', fontSize: 14 }}>
              ยังไม่มีข้อมูล maturity profile จาก Actuary — กรอกได้ที่หน้า Upload รายงาน
              {disclosure.weightedAvgDuration != null
                ? ` · อายุเฉลี่ยถ่วงน้ำหนัก ${disclosure.weightedAvgDuration.toFixed(1)} ปี`
                : ''}
            </div>
          )}
        </div>
      </div>

      {disclosure.departureNote && (
        <div
          style={{
            padding: '12px 16px',
            background: '#fef3e8',
            border: '1px solid #f0dab0',
            borderLeft: '4px solid var(--warning)',
            borderRadius: 8,
            marginTop: 'var(--section-gap)',
            fontSize: 14,
            lineHeight: 1.55,
          }}
        >
          {disclosure.departureNote}
        </div>
      )}

      <div
        style={{
          padding: '12px 16px',
          background: disclosure.glReconciliation.tiedOut ? '#e7f4ec' : '#fce9e7',
          border: `1px solid ${disclosure.glReconciliation.tiedOut ? '#bbdebf' : '#f4c1bd'}`,
          borderRadius: 8,
          marginTop: 14,
          fontSize: 14,
        }}
      >
        <b>การกระทบยอด GL:</b> ยอดบัญชี {disclosure.glReconciliation.liabilityAccountCode} ={' '}
        {fmt(disclosure.glReconciliation.glLiabilityBalance)} · roll-forward ={' '}
        {fmt(disclosure.glReconciliation.walkClosing)} ·{' '}
        {disclosure.glReconciliation.tiedOut
          ? 'ตรงกัน ✓'
          : `ผลต่าง ${fmt(disclosure.glReconciliation.difference)} — ต้องตรวจสอบ`}
      </div>

      <WhatIfPanel companyId={companyId} valuationId={valuationId} />
    </>
  );
}

// What-if sensitivity tool (NEW — not in the prototype). Estimates the DBO under
// assumption shifts from the actuary's stored deltas + duration approximation.
// Clearly labelled non-disclosure.
function WhatIfPanel({
  companyId,
  valuationId,
}: {
  companyId: string | undefined;
  valuationId: string | undefined;
}) {
  const whatIf = useEmpBenefitWhatIf(companyId);
  const [discountDelta, setDiscountDelta] = useState<number>(0);
  const [salaryDelta, setSalaryDelta] = useState<number>(0);
  const [turnoverDelta, setTurnoverDelta] = useState<number>(0);
  const [mortalityDelta, setMortalityDelta] = useState<number>(0);

  const run = (): void => {
    if (!valuationId) return;
    whatIf.mutate({ valuationId, discountDelta, salaryDelta, turnoverDelta, mortalityDelta });
  };

  const slider = (
    label: string,
    value: number,
    set: (n: number) => void,
    min: number,
    max: number,
    step: number,
    unit: string,
  ) => (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '180px 1fr 70px',
        gap: 10,
        alignItems: 'center',
      }}
    >
      <span style={{ fontSize: 14 }}>{label}</span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => set(Number(e.target.value))}
      />
      <span className="tnum" style={{ fontSize: 14, textAlign: 'right' }}>
        {value > 0 ? '+' : ''}
        {value}
        {unit}
      </span>
    </div>
  );

  const result = whatIf.data;
  const delta = result ? result.estimatedDbo - result.baseDbo : 0;

  return (
    <div className="card" style={{ marginTop: 'var(--section-gap)' }}>
      <div className="card-head">
        <h3>เครื่องมือจำลอง What-if</h3>
        <span className="meta">ประมาณการ — ไม่ใช่ตัวเลข disclosure</span>
      </div>
      <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {slider('อัตราคิดลด', discountDelta, setDiscountDelta, -2, 2, 0.25, '%')}
        {slider('อัตราเพิ่มเงินเดือน', salaryDelta, setSalaryDelta, -2, 2, 0.25, '%')}
        {slider('อัตราการลาออก', turnoverDelta, setTurnoverDelta, -2, 2, 0.25, '%')}
        {slider('อายุคาดหมาย (มรณะ)', mortalityDelta, setMortalityDelta, -3, 3, 1, ' ปี')}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button className="btn primary" onClick={run} disabled={!valuationId || whatIf.isPending}>
            <Icon name="check" size={14} /> {whatIf.isPending ? 'กำลังประเมิน…' : 'ประเมิน DBO'}
          </button>
          {!valuationId && <span className="meta">ต้องมีรายงาน Actuarial ก่อน</span>}
        </div>
        {result && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr',
              gap: 14,
              padding: '14px 16px',
              background: '#fafafa',
              borderRadius: 8,
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div className="meta">DBO ฐาน</div>
              <div className="tnum" style={{ fontSize: 20, fontWeight: 700, marginTop: 4 }}>
                {fmt(result.baseDbo)}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div className="meta">DBO ประมาณการ</div>
              <div
                className="tnum"
                style={{ fontSize: 20, fontWeight: 700, marginTop: 4, color: 'var(--brand)' }}
              >
                {fmt(result.estimatedDbo)}
              </div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div className="meta">เปลี่ยนแปลง</div>
              <div
                className="tnum"
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  marginTop: 4,
                  color: delta < 0 ? 'var(--positive)' : 'var(--negative)',
                }}
              >
                {delta > 0 ? '+' : ''}
                {fmt(delta)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface UploadReportModalProps {
  companyId: string | undefined;
  onClose: () => void;
}

// ── Actuary sensitivity + maturity entry builders (IAS 19.145 / 147(c)) ──
// Pure + exported so they can be unit-tested without poking the DOM.
interface SensDeltaInput {
  plus: string;
  minus: string;
}
type SensState = Record<'discountRate' | 'salaryGrowth' | 'turnover' | 'mortality', SensDeltaInput>;
type MatState = Record<
  'withinOneYear' | 'oneToTwoYears' | 'twoToFiveYears' | 'beyondFiveYears',
  string
>;

// Strict numeric coercion: empty/whitespace/garbage/±Infinity all collapse to 0.
// (`Number(x) || 0` alone would let "1e999" → Infinity slip through, since
// Infinity is truthy and passes the schema's .nonnegative().)
function toFiniteNumber(s: string): number {
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

// An assumption is emitted only when the actuary filled at least one side; an
// empty side coerces to 0. Returns null when nothing at all was entered (so the
// disclosure correctly shows "no data" rather than a row of zeros).
export function buildSensitivityInput(s: SensState): EmpBenefitSensitivityInput | null {
  const out: EmpBenefitSensitivityInput = {};
  (Object.keys(s) as (keyof SensState)[]).forEach((k) => {
    const d = s[k];
    if (d.plus.trim() === '' && d.minus.trim() === '') return;
    out[k] = { plus: toFiniteNumber(d.plus), minus: toFiniteNumber(d.minus) };
  });
  return Object.keys(out).length ? out : null;
}

// Single source of truth for the maturity-profile total (used by the disclosure
// card render + the xlsx export so they can never silently diverge).
export function maturityTotal(p: EmpBenefitMaturityProfile): number {
  return p.withinOneYear + p.oneToTwoYears + p.twoToFiveYears + p.beyondFiveYears;
}

// Emits the full 4-band profile when ANY band is filled (empty bands → 0);
// null when all four are blank.
export function buildMaturityInput(m: MatState): EmpBenefitMaturityProfile | null {
  const keys = Object.keys(m) as (keyof MatState)[];
  if (keys.every((k) => m[k].trim() === '')) return null;
  return {
    withinOneYear: toFiniteNumber(m.withinOneYear),
    oneToTwoYears: toFiniteNumber(m.oneToTwoYears),
    twoToFiveYears: toFiniteNumber(m.twoToFiveYears),
    beyondFiveYears: toFiniteNumber(m.beyondFiveYears),
  };
}

export function UploadReportModal({ companyId, onClose }: UploadReportModalProps) {
  const createMutation = useCreateEmpBenefitValuation(companyId);
  const uploadPdfMutation = useUploadEmpBenefitValuationPdf(companyId);
  // Signed actuarial-report PDF (optional). Uploaded AFTER the valuation row is
  // created (needs its id). createdId guards against re-creating the valuation if
  // only the upload failed and the user retries.
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [createdId, setCreatedId] = useState<string | null>(null);

  const today = new Date().toISOString().slice(0, 10);
  const [valuationDate, setValuationDate] = useState<string>('2026-12-31');
  const [discountRate, setDiscountRate] = useState<string>('2.95');
  const [salaryGrowthRate, setSalaryGrowthRate] = useState<string>('5.50');
  const [totalDbo, setTotalDbo] = useState<string>('148000000');
  const [employeeCount, setEmployeeCount] = useState<string>('1240');
  const [source, setSource] = useState<EmpBenefitSource>('ACTUARIAL_REPORT');
  const [reportRef, setReportRef] = useState<string>('AV-2026-001');
  // Phase 42 optional-3: GL dimensions carried onto the monthly accrual JV.
  const [branchId, setBranchId] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<string>('');
  // v2 IAS 19 drivers — projectedServiceCost + openingDbo are REQUIRED for a
  // correct monthly accrual (SC = projectedServiceCost/12, IC = net interest on
  // openingDbo); the rest feed the disclosure assumptions.
  const [projectedServiceCost, setProjectedServiceCost] = useState<string>('18000000');
  const [openingDbo, setOpeningDbo] = useState<string>('140000000');
  const [turnoverRate, setTurnoverRate] = useState<string>('9.10');
  const [mortalityTable, setMortalityTable] = useState<string>('TMO22');
  const [retirementAge, setRetirementAge] = useState<string>('60');
  const [weightedAvgDuration, setWeightedAvgDuration] = useState<string>('12.5');
  // Optional actuary disclosures — sensitivity (IAS 19.145) + maturity (147(c)).
  // Left blank by default: only the actuary's report carries them.
  const [sens, setSens] = useState<SensState>({
    discountRate: { plus: '', minus: '' },
    salaryGrowth: { plus: '', minus: '' },
    turnover: { plus: '', minus: '' },
    mortality: { plus: '', minus: '' },
  });
  const [maturity, setMaturity] = useState<MatState>({
    withinOneYear: '',
    oneToTwoYears: '',
    twoToFiveYears: '',
    beyondFiveYears: '',
  });
  const setSensField = (k: keyof SensState, side: 'plus' | 'minus', v: string): void =>
    setSens((prev) => ({ ...prev, [k]: { ...prev[k], [side]: v } }));
  const setMatField = (k: keyof MatState, v: string): void =>
    setMaturity((prev) => ({ ...prev, [k]: v }));

  const handleSubmit = (): void => {
    if (!companyId) return;
    const payload: CreateEmpBenefitValuationV2 = {
      planCode: 'SEV',
      valuationDate,
      asOfDate: valuationDate,
      totalDbo: Number(totalDbo),
      plannedAssetsValue: 0,
      discountRate: Number(discountRate),
      salaryGrowthRate: Number(salaryGrowthRate),
      employeeCount: Number(employeeCount),
      source,
      reportRef: reportRef || null,
      branchId: branchId || null,
      departmentId: departmentId || null,
      projectedServiceCost: Number(projectedServiceCost) || 0,
      openingDbo: Number(openingDbo) || 0,
      pastServiceCost: 0,
      turnoverRate: turnoverRate ? Number(turnoverRate) : null,
      mortalityTable: mortalityTable || null,
      retirementAge: retirementAge ? Number(retirementAge) : null,
      weightedAvgDuration: weightedAvgDuration ? Number(weightedAvgDuration) : null,
      sensitivity: buildSensitivityInput(sens),
      maturityProfile: buildMaturityInput(maturity),
    };
    // If the valuation was already created (a prior PDF upload failed), retry the
    // upload only — never re-create the row.
    if (createdId) {
      if (pdfFile) {
        uploadPdfMutation.mutate({ id: createdId, file: pdfFile }, { onSuccess: () => onClose() });
      } else {
        onClose();
      }
      return;
    }
    createMutation.mutate(payload, {
      onSuccess: (created) => {
        const id = created.id;
        setCreatedId(id);
        if (pdfFile) {
          uploadPdfMutation.mutate({ id, file: pdfFile }, { onSuccess: () => onClose() });
        } else {
          onClose();
        }
      },
    });
  };

  void today;

  const fieldLabel = (text: string) => (
    <label
      style={{
        fontSize: 14,
        color: 'var(--text-muted)',
        fontWeight: 500,
        display: 'block',
        marginBottom: 6,
      }}
    >
      {text}
    </label>
  );
  const numInput = { width: '100%', height: 38, fontSize: 14, textAlign: 'right' as const };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 780,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              Upload รายงาน Actuarial ใหม่
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              เลขที่ใหม่ <b style={{ color: '#3f3f46' }}>{reportRef}</b> · จะถูกใช้สำหรับลงบัญชีปี{' '}
              {Number(valuationDate.slice(0, 4)) + 1}
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div
          style={{
            padding: '22px',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            overflow: 'auto',
            flex: 1,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                Actuary / บริษัทผู้คำนวณ
              </label>
              <select
                className="select"
                style={{ width: '100%', height: 38, fontSize: 14 }}
                value={source}
                onChange={(e) => setSource(e.target.value as EmpBenefitSource)}
              >
                <option value="ACTUARIAL_REPORT">External Actuary (WTW/Milliman/Aon)</option>
                <option value="MANUAL">Manual entry — internal estimate</option>
              </select>
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                ณ วันที่ประเมิน
              </label>
              <input
                className="input"
                type="date"
                value={valuationDate}
                onChange={(e) => setValuationDate(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              เลขที่รายงาน (Report Ref)
            </label>
            <input
              className="input"
              type="text"
              value={reportRef}
              onChange={(e) => setReportRef(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                สาขา (Branch)
              </label>
              <BranchSelect
                companyId={companyId}
                value={branchId}
                onChange={setBranchId}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                หน่วยงาน (Cost Center)
              </label>
              <DepartmentSelect
                companyId={companyId}
                value={departmentId}
                onChange={setDepartmentId}
                branchId={branchId}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>

          <div className="section-title" style={{ margin: '8px 0 4px' }}>
            สมมติฐานหลัก (Key Assumptions)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                Discount Rate (%/ปี)
              </label>
              <input
                className="input tnum"
                type="number"
                step="0.01"
                value={discountRate}
                onChange={(e) => setDiscountRate(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                อัตราเพิ่มเงินเดือน (%/ปี)
              </label>
              <input
                className="input tnum"
                type="number"
                step="0.01"
                value={salaryGrowthRate}
                onChange={(e) => setSalaryGrowthRate(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                จำนวนพนักงานในแผน
              </label>
              <input
                className="input tnum"
                type="number"
                value={employeeCount}
                onChange={(e) => setEmployeeCount(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                DBO ที่คำนวณได้ (บาท)
              </label>
              <input
                className="input tnum"
                type="number"
                value={totalDbo}
                onChange={(e) => setTotalDbo(e.target.value)}
                style={{
                  width: '100%',
                  height: 38,
                  fontSize: 15,
                  textAlign: 'right',
                  fontWeight: 600,
                }}
              />
            </div>
          </div>

          <div className="section-title" style={{ margin: '8px 0 4px' }}>
            ตัวขับเคลื่อน IAS 19 (ใช้คำนวณรับรู้รายเดือน)
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              {fieldLabel('ต้นทุนบริการปัจจุบันรายปี (Projected SC) — ขับ SC')}
              <input
                className="input tnum"
                type="number"
                value={projectedServiceCost}
                onChange={(e) => setProjectedServiceCost(e.target.value)}
                style={{ ...numInput, fontWeight: 600 }}
              />
            </div>
            <div>
              {fieldLabel('DBO ต้นงวด (Opening DBO) — ขับ IC')}
              <input
                className="input tnum"
                type="number"
                value={openingDbo}
                onChange={(e) => setOpeningDbo(e.target.value)}
                style={{ ...numInput, fontWeight: 600 }}
              />
            </div>
            <div>
              {fieldLabel('อัตราการลาออก (%/ปี)')}
              <input
                className="input tnum"
                type="number"
                step="0.01"
                value={turnoverRate}
                onChange={(e) => setTurnoverRate(e.target.value)}
                style={numInput}
              />
            </div>
            <div>
              {fieldLabel('อายุเฉลี่ยถ่วงน้ำหนัก (ปี)')}
              <input
                className="input tnum"
                type="number"
                step="0.1"
                value={weightedAvgDuration}
                onChange={(e) => setWeightedAvgDuration(e.target.value)}
                style={numInput}
              />
            </div>
            <div>
              {fieldLabel('ตารางมรณะ')}
              <input
                className="input"
                type="text"
                value={mortalityTable}
                onChange={(e) => setMortalityTable(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              {fieldLabel('อายุเกษียณ (ปี)')}
              <input
                className="input tnum"
                type="number"
                value={retirementAge}
                onChange={(e) => setRetirementAge(e.target.value)}
                style={numInput}
              />
            </div>
          </div>

          <div className="section-title" style={{ margin: '8px 0 4px' }}>
            การวิเคราะห์ความอ่อนไหว (Sensitivity) — IAS 19.145 · ผลกระทบต่อ DBO (บาท · ถ้า Actuary
            ระบุ)
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr 1fr 1fr',
              gap: 8,
              alignItems: 'center',
            }}
          >
            <div />
            <div style={{ fontSize: 13, color: 'var(--text-faint)', textAlign: 'right' }}>
              +0.5% / +1 ปี
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-faint)', textAlign: 'right' }}>
              −0.5% / −1 ปี
            </div>
            {(
              [
                ['discountRate', 'อัตราคิดลด'],
                ['salaryGrowth', 'อัตราเพิ่มเงินเดือน'],
                ['turnover', 'อัตราการลาออก'],
                ['mortality', 'ตารางมรณะ (±1 ปี)'],
              ] as [keyof SensState, string][]
            ).map(([k, label]) => (
              <Fragment key={k}>
                <div style={{ fontSize: 14 }}>{label}</div>
                <input
                  className="input tnum"
                  type="number"
                  aria-label={`sensitivity ${k} plus`}
                  value={sens[k].plus}
                  onChange={(e) => setSensField(k, 'plus', e.target.value)}
                  style={numInput}
                />
                <input
                  className="input tnum"
                  type="number"
                  aria-label={`sensitivity ${k} minus`}
                  value={sens[k].minus}
                  onChange={(e) => setSensField(k, 'minus', e.target.value)}
                  style={numInput}
                />
              </Fragment>
            ))}
          </div>

          <div className="section-title" style={{ margin: '8px 0 4px' }}>
            ประมาณการจ่ายผลประโยชน์ในอนาคต (Maturity Profile) — IAS 19.147(c) · บาท · ถ้า Actuary
            ระบุ
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
            {(
              [
                ['withinOneYear', 'ภายใน 1 ปี'],
                ['oneToTwoYears', '1 – 2 ปี'],
                ['twoToFiveYears', '2 – 5 ปี'],
                ['beyondFiveYears', 'เกิน 5 ปี'],
              ] as [keyof MatState, string][]
            ).map(([k, label]) => (
              <div key={k}>
                {fieldLabel(label)}
                <input
                  className="input tnum"
                  type="number"
                  min={0}
                  aria-label={`maturity ${k}`}
                  value={maturity[k]}
                  onChange={(e) => setMatField(k, e.target.value)}
                  style={numInput}
                />
              </div>
            ))}
          </div>

          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              แนบรายงาน PDF (รับรองโดย Fellow Actuary) — ไม่บังคับ
            </label>
            <input
              type="file"
              accept="application/pdf"
              aria-label="actuary report pdf"
              onChange={(e) => setPdfFile(e.target.files?.[0] ?? null)}
              style={{ display: 'block', fontSize: 14 }}
            />
            {pdfFile && (
              <div className="meta" style={{ marginTop: 4 }}>
                เลือกแล้ว: <b style={{ color: '#3f3f46' }}>{pdfFile.name}</b> (
                {Math.round(pdfFile.size / 1024)} KB) · จะอัปโหลดหลังบันทึก valuation
              </div>
            )}
            {createdId && (
              <div style={{ marginTop: 4, fontSize: 13, color: 'var(--negative)' }}>
                บันทึก valuation แล้ว แต่แนบ PDF ไม่สำเร็จ — กด “บันทึก” อีกครั้งเพื่อลองอัปโหลดใหม่
              </div>
            )}
          </div>

          <div
            style={{
              padding: '12px 14px',
              background: '#e8eff8',
              borderRadius: 6,
              fontSize: 13.5,
              lineHeight: 1.55,
              display: 'flex',
              gap: 10,
            }}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: 'var(--info)',
                color: 'white',
                display: 'grid',
                placeItems: 'center',
                fontSize: 14,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              i
            </div>
            <div>
              เมื่อบันทึกแล้ว ระบบจะ: (1) ตั้ง valuation เป็นค่าปัจจุบันสำหรับ DBO + assumptions (2)
              ใช้สำหรับการรัน TAS 19 ประจำเดือนทันที (3) เปรียบเทียบกับรายงานปีก่อน → คำนวณ
              Actuarial Gain/Loss เข้า OCI ในงวดที่ตัดบัญชี
            </div>
          </div>

          {createMutation.error && (
            <div style={{ color: 'var(--negative)', fontSize: 13 }}>
              เกิดข้อผิดพลาด: {createMutation.error.message}
            </div>
          )}
          {uploadPdfMutation.error && (
            <div style={{ color: 'var(--negative)', fontSize: 13 }}>
              อัปโหลด PDF ไม่สำเร็จ: {uploadPdfMutation.error.message}
            </div>
          )}
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            onClick={handleSubmit}
            disabled={!companyId || createMutation.isPending || uploadPdfMutation.isPending}
          >
            <Icon name="check" size={14} />{' '}
            {createMutation.isPending
              ? 'กำลังบันทึก…'
              : uploadPdfMutation.isPending
                ? 'กำลังอัปโหลด PDF…'
                : createdId
                  ? 'อัปโหลด PDF อีกครั้ง'
                  : 'บันทึก Valuation'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface RecognizeModalProps {
  curr: ActuaryReport;
  currMove: ActuaryMovement;
  isPending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

function RecognizeModal({ curr, isPending, onConfirm, onClose }: RecognizeModalProps) {
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 760,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              รัน TAS 19 ประจำเดือน — งวด {DEMO_PERIOD.slice(0, 4)}-{DEMO_PERIOD.slice(4, 6)}
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              เสนอรายการรับรู้รายเดือนเป็น DRAFT (ส่งอนุมัติก่อนลง JV) · อ้างอิง {curr.id}
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div
            style={{
              padding: '14px 16px',
              background: '#fafafa',
              borderRadius: 8,
              border: '1px solid var(--border)',
            }}
          >
            <div className="section-title" style={{ margin: '0 0 10px' }}>
              วิธีคำนวณ (IAS 19)
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.6 }}>
              ระบบจะคำนวณจากรายงาน actuary ที่ adopt แล้ว (ไม่ใช่กรอกเอง):
              <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                <li>
                  <b>Service Cost</b> = ต้นทุนบริการปัจจุบัน (projected service cost จาก actuary) ÷
                  12
                </li>
                <li>
                  <b>Interest Cost</b> = (DBO ต้นงวด − สินทรัพย์โครงการ) × อัตราคิดลด ÷ 12
                </li>
              </ul>
            </div>
          </div>

          <div className="section-title" style={{ margin: '8px 0 4px' }}>
            JV ที่จะลงเมื่ออนุมัติขั้นสุดท้าย (split)
          </div>
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>บัญชี</th>
                  <th>ชื่อบัญชี</th>
                  <th>เดบิต/เครดิต</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="code">60110092</td>
                  <td>ค่าใช้จ่ายผลประโยชน์พนักงาน (Service Cost)</td>
                  <td>เดบิต</td>
                </tr>
                <tr>
                  <td className="code">60200004</td>
                  <td>ดอกเบี้ยจ่าย (Interest Cost)</td>
                  <td>เดบิต</td>
                </tr>
                <tr>
                  <td className="code">20170027</td>
                  <td>ภาระผูกพันผลประโยชน์พนักงาน</td>
                  <td>เครดิต (SC + IC)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            style={{
              padding: '12px 14px',
              background: '#e8eff8',
              borderRadius: 6,
              fontSize: 13.5,
              lineHeight: 1.55,
            }}
          >
            การกดนี้จะ <b>เสนอ (propose)</b> รายการเป็น DRAFT — <b>ยังไม่ลง JV</b> — จำนวน SC/IC
            ที่ระบบคำนวณจะแสดงในตารางงวด แล้วต้องผ่านการอนุมัติ (maker-checker) ก่อนจึงลง JV จริง ·
            idempotent ต่องวด — เสนอซ้ำไม่สร้างซ้ำ
          </div>
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button className="btn primary" onClick={onConfirm} disabled={isPending}>
            <Icon name="check" size={14} /> {isPending ? 'กำลังเสนอ…' : 'เสนอรายการ (DRAFT)'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function SettleModal({
  companyId,
  onClose,
}: {
  companyId: string | undefined;
  onClose: () => void;
}) {
  const createMutation = useCreateEmpBenefitSettlement(companyId);

  const [employeeRef, setEmployeeRef] = useState<string>('');
  const [employeeName, setEmployeeName] = useState<string>('');
  const [department, setDepartment] = useState<string>('');
  const [position, setPosition] = useState<string>('');
  const [hireDate, setHireDate] = useState<string>('');
  const [settlementDate, setSettlementDate] = useState<string>('2026-06-30');
  const [finalMonthlyWage, setFinalMonthlyWage] = useState<string>('');
  const [carriedProvision, setCarriedProvision] = useState<string>('');
  const [reason, setReason] = useState<EmpBenefitSettlementReason>('RETIREMENT');

  // Live preview (mirrors the backend LPA ladder; authoritative compute is server-side).
  const wage = Number(finalMonthlyWage) || 0;
  const carried = Number(carriedProvision) || 0;
  const hire = hireDate ? new Date(hireDate + 'T00:00:00Z') : null;
  const settle = settlementDate ? new Date(settlementDate + 'T00:00:00Z') : null;
  const validDates = !!hire && !!settle && settle >= hire;
  const days = validDates ? previewSeveranceDays(hire, settle) : 0;
  const years = validDates ? previewCompletedYears(hire, settle) : 0;
  const severance = validDates ? previewSeverance(wage, days) : 0;
  const diff = carried - severance; // settlementGainLoss (+ = gain → P&L credit)

  const canSubmit =
    !!companyId &&
    employeeRef.trim() !== '' &&
    employeeName.trim() !== '' &&
    validDates &&
    wage > 0 &&
    !createMutation.isPending;

  const handleSubmit = (): void => {
    if (!canSubmit) return;
    const payload: CreateEmpBenefitSettlement = {
      planCode: 'SEV',
      employeeRef: employeeRef.trim(),
      employeeName: employeeName.trim(),
      department: department.trim() || null,
      position: position.trim() || null,
      hireDate,
      settlementDate,
      finalMonthlyWage: wage,
      carriedProvision: carried,
      reason,
      isDraft: false,
    };
    createMutation.mutate(payload, { onSuccess: () => onClose() });
  };

  const labelStyle = {
    fontSize: 13,
    color: 'var(--text-muted)',
    fontWeight: 500,
    display: 'block',
    marginBottom: 5,
  } as const;
  const inputStyle = { width: '100%', height: 38, fontSize: 14 } as const;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 900,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              บันทึกการชำระผลประโยชน์ (Settlement)
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              กรอกข้อมูลพนักงาน — ระบบคำนวณค่าชดเชยตาม LPA ladder · สร้างเป็น DRAFT
              รอการอนุมัติก่อนลงบัญชี
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div
          style={{
            padding: '22px',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            overflow: 'auto',
            flex: 1,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
            <div>
              <label style={labelStyle}>รหัสพนักงาน *</label>
              <input
                className="input"
                style={inputStyle}
                value={employeeRef}
                onChange={(e) => setEmployeeRef(e.target.value)}
                placeholder="EMP-0024"
              />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={labelStyle}>ชื่อ-นามสกุล *</label>
              <input
                className="input"
                style={inputStyle}
                value={employeeName}
                onChange={(e) => setEmployeeName(e.target.value)}
                placeholder="สมศักดิ์ จันทร์ส่อง"
              />
            </div>
            <div>
              <label style={labelStyle}>แผนก</label>
              <input
                className="input"
                style={inputStyle}
                value={department}
                onChange={(e) => setDepartment(e.target.value)}
                placeholder="โรงงาน 1"
              />
            </div>
            <div>
              <label style={labelStyle}>ตำแหน่ง</label>
              <input
                className="input"
                style={inputStyle}
                value={position}
                onChange={(e) => setPosition(e.target.value)}
                placeholder="หัวหน้าฝ่ายเตา"
              />
            </div>
            <div>
              <label style={labelStyle}>สาเหตุการพ้นสภาพ *</label>
              <select
                className="select"
                style={inputStyle}
                value={reason}
                onChange={(e) => setReason(e.target.value as EmpBenefitSettlementReason)}
              >
                <option value="RETIREMENT">เกษียณอายุ</option>
                <option value="INVOLUNTARY">เลิกจ้าง (ได้สิทธิยกเว้นภาษี ม.ร.394)</option>
                <option value="FIXED_TERM_END">ครบกำหนดสัญญา</option>
                <option value="VOLUNTARY">ลาออก</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>วันเริ่มงาน *</label>
              <input
                type="date"
                className="input"
                style={inputStyle}
                value={hireDate}
                onChange={(e) => setHireDate(e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>วันพ้นสภาพ *</label>
              <input
                type="date"
                className="input"
                style={inputStyle}
                value={settlementDate}
                onChange={(e) => setSettlementDate(e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle}>เงินเดือนสุดท้าย *</label>
              <input
                type="number"
                className="input"
                style={inputStyle}
                value={finalMonthlyWage}
                onChange={(e) => setFinalMonthlyWage(e.target.value)}
                placeholder="88000"
              />
            </div>
            <div>
              <label style={labelStyle}>Provision สะสม (จาก Actuary)</label>
              <input
                type="number"
                className="input"
                style={inputStyle}
                value={carriedProvision}
                onChange={(e) => setCarriedProvision(e.target.value)}
                placeholder="1120000"
              />
            </div>
          </div>

          {!validDates && (hireDate !== '' || settlementDate !== '') && (
            <div className="meta" style={{ color: 'var(--negative)' }}>
              วันพ้นสภาพต้องไม่ก่อนวันเริ่มงาน
            </div>
          )}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto 1fr auto 1fr',
              gap: 14,
              alignItems: 'center',
              padding: '14px 16px',
              background: '#fafafa',
              borderRadius: 8,
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div className="meta">ค่าชดเชยตามกฎหมาย (LPA)</div>
              <div className="meta">
                {days} วัน · อายุงาน {years} ปี
              </div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  marginTop: 4,
                  color: 'var(--brand)',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(severance)}
              </div>
            </div>
            <div
              style={{
                fontSize: 28,
                color: 'var(--text-faint)',
                fontFamily: 'var(--font-display)',
              }}
            >
              vs
            </div>
            <div style={{ textAlign: 'center' }}>
              <div className="meta">Provision สะสม</div>
              <div className="meta">จาก Actuary</div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  marginTop: 4,
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {fmt(carried)}
              </div>
            </div>
            <div
              style={{
                fontSize: 22,
                color: diff > 0 ? 'var(--positive)' : 'var(--negative)',
                fontFamily: 'var(--font-display)',
              }}
            >
              {diff > 0 ? '↑' : '↓'}
            </div>
            <div style={{ textAlign: 'center' }}>
              <div className="meta">ส่วนต่าง (P&L)</div>
              <div className="meta">{diff >= 0 ? 'Settlement Gain' : 'Settlement Loss'}</div>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  marginTop: 4,
                  color: diff > 0 ? 'var(--positive)' : 'var(--negative)',
                  fontFamily: 'var(--font-display)',
                }}
                className="tnum"
              >
                {diff > 0 ? '+' : ''}
                {fmt(diff)}
              </div>
            </div>
          </div>

          <div className="section-title" style={{ margin: '4px 0' }}>
            JV ที่จะสร้างเมื่ออนุมัติ (ALL_TO_PL)
          </div>
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th>บัญชี</th>
                  <th>ชื่อบัญชี</th>
                  <th className="num">เดบิต</th>
                  <th className="num">เครดิต</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="code">20170027</td>
                  <td>ภาระผูกพันผลประโยชน์พนักงาน — {employeeRef || '—'}</td>
                  <td className="num tnum">{fmt(carried)}</td>
                  <td className="num">—</td>
                </tr>
                <tr>
                  <td className="code muted">—</td>
                  <td>เงินฝากธนาคาร บัญชีหลัก (จ่ายค่าชดเชย — gross)</td>
                  <td className="num">—</td>
                  <td className="num tnum">{fmt(severance)}</td>
                </tr>
                {diff > 0 && (
                  <tr>
                    <td className="code">40130015</td>
                    <td>กำไรจากการชำระผลประโยชน์ (P&L)</td>
                    <td className="num">—</td>
                    <td className="num tnum">{fmt(diff)}</td>
                  </tr>
                )}
                {diff < 0 && (
                  <tr>
                    <td className="code">40130015</td>
                    <td>ขาดทุนจากการชำระผลประโยชน์ (P&L)</td>
                    <td className="num tnum">{fmt(-diff)}</td>
                    <td className="num">—</td>
                  </tr>
                )}
                <tr className="total">
                  <td colSpan={2}>รวม</td>
                  <td className="num tnum">{fmt(carried + Math.max(0, -diff))}</td>
                  <td className="num tnum">{fmt(severance + Math.max(0, diff))}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div
            style={{
              padding: '12px 14px',
              background: '#fef3e8',
              border: '1px solid #f0dab0',
              borderLeft: '4px solid var(--warning)',
              borderRadius: 6,
              fontSize: 14,
              lineHeight: 1.55,
            }}
          >
            <b>หมายเหตุนโยบาย (ALL_TO_PL):</b> กำไร/ขาดทุนจากการชำระรับรู้เข้า P&L ทั้งจำนวน —
            เบี่ยงเบนจาก IAS 19.111 โดยเจตนา (อนุมัติโดยผู้มีอำนาจ) · ภาษีหัก ณ ที่จ่าย/ภงด.1
            จัดการในระบบเงินเดือน (จ่าย gross)
          </div>
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button className="btn primary" onClick={handleSubmit} disabled={!canSubmit}>
            <Icon name="check" size={14} />{' '}
            {createMutation.isPending ? 'กำลังบันทึก…' : 'สร้างรายการ settlement (DRAFT)'}
          </button>
        </div>
        {createMutation.isError && (
          <div
            className="meta"
            style={{ padding: '0 22px 14px', color: 'var(--negative)', background: '#fafafa' }}
          >
            บันทึกไม่สำเร็จ — ตรวจสอบข้อมูลแล้วลองใหม่
          </div>
        )}
      </div>
    </div>
  );
}

// Mark unused EB_PLANS as referenced for future use without changing exports.
void EB_PLANS;
